/**
 * The lamp-hook-sabre art (id lamp-hook-sabre; display name from the game data), hand-made in the sample-page style (replaces
 * the C3 generator skeleton). The art's own look: the IRON BUTTERFLY of the lamp guards. Cold steel wings (white leading
 * edge, a lit band, a dark core by the body, a gold brocade spot) over maroon silk hind wings; cuts shaped like a wing
 * (two lobes, a notch, a maroon silk lining under the steel edge); brocade silk ribbons that twist maroon / gold; a gold
 * lattice weave on the ground. The bleed (kept, user 10-13) is told apart from heat-sabre / tongs-claw: no splashes or
 * drops, a red silk thread pulled out of the wound and a small crimson butterfly pinned on the foe per stack, which
 * jy-execute calls off the foe into the blade. No glyph circle, no beams. Each move is staged anticipation -> strike ->
 * impact -> dissipation; body-moving moves leave split-body afterimages. Roles from the data: jy-cross is the lock
 * (lockForms), jy-whirl lands on a picked spot, jy-lunge a dash to the target, jy-shatter the break, jy-oath the
 * guard-wait buff, jy-execute the cash-out, jy-dragon the ท่าใหญ่ (leap template, timed to its beats in
 * render/fx/longBrocade.ts). Original designs.
 */
import { add, diamond, hash, isoRing, lerp, line, px, unit, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { bladeArc, C, clamp01, fade, hitStar, level, ringOn, since, strikeAt, tilesPx, type ArtFx } from '../families/swordKit';

// ── Tones (looked up once) ──

const WH = C('white', 8);
const ST1 = C('steel', 1);
const ST2 = C('steel', 2);
const ST3 = C('steel', 3);
const ST4 = C('steel', 4);
const ST5 = C('steel', 5);
const ST6 = C('steel', 6);
const M2 = C('maroon', 2);
const M3 = C('maroon', 3);
const M4 = C('maroon', 4);
const M5 = C('maroon', 5);
const M6 = C('maroon', 6);
const M7 = C('maroon', 7);
const TR2 = C('trim', 2);
const TR3 = C('trim', 3);
const TR4 = C('trim', 4);
const TR5 = C('trim', 5);
const TR6 = C('trim', 6);

// ── The art's pieces ──

type Kind = 'iron' | 'blood' | 'gold';
interface WingTones {
  edge: string;
  lit: string;
  shade: string;
  hind: string;
  hindEdge: string;
  spot: string;
  body: string;
}
const TONES: Record<Kind, WingTones> = {
  iron: { edge: WH, lit: ST4, shade: ST2, hind: M4, hindEdge: M6, spot: TR5, body: ST1 },
  blood: { edge: M7, lit: M5, shade: M3, hind: M2, hindEdge: M4, spot: WH, body: C('ink', 1) },
  gold: { edge: TR6, lit: TR4, shade: TR2, hind: M3, hindEdge: M5, spot: WH, body: C('brown', 1) },
};

/**
 * A butterfly at `c`, `r` cells from the body to a fore wing's apex, the head along screen angle `rot`, the wings spread
 * by `open` (1 flat open, toward 0 closing edge-on: the flutter), squashed by `sy` (1 upright, 0.5 lying on the ground).
 * Fore wings: a pointed fan swept toward the head (an edge row, a lit outer band, a dark inner band, two dark veins, a
 * brocade spot); hind wings rounder and smaller in the lining tone. `etch`: the edges, veins and spots only (a print on
 * the ground). Cells are filled tone run by tone run (one style change per run, not per cell: the pen allocates per style).
 */
function butterfly(g: G, c: V, r: number, rot: number, open: number, lvl: number, kind: Kind = 'iron', sy = 1, etch = false): void {
  if (lvl <= 0 || r < 2) return;
  const T = TONES[kind];
  const hx = Math.cos(rot);
  const hy = Math.sin(rot);
  const span = Math.max(0.15, Math.min(1, Math.abs(open)));
  const ed = 1.25 / r;
  const vw = 0.55 / r;
  const B = Math.ceil(r * 1.05) + 1;
  const By = Math.ceil(B * sy);
  const cx = Math.round(c[0]);
  const cy = Math.round(c[1]);
  let cur = '';
  level(g, lvl);
  for (let y = -By; y <= By; y++)
    for (let x = -B; x <= B; x++) {
      const yy = y / sy;
      const along = (x * hx + yy * hy) / r;
      const side = Math.abs(-x * hy + yy * hx) / r;
      let col: string | null = null;
      if (side * r <= 0.55 && along > -0.45 && along < 0.5) col = along > 0.34 ? T.edge : T.body;
      else {
        const sx = side / span;
        const rho = Math.hypot(sx, along);
        const th = Math.atan2(along, sx);
        if (th > -0.12 && th < 1.3) {
          const R = 0.42 + 0.58 * Math.pow(Math.sin((Math.PI * (th + 0.12)) / 1.42), 0.55);
          if (rho <= R)
            col =
              rho > R - ed
                ? T.edge
                : r >= 6 && Math.abs(th - 0.95) < 0.17 && Math.abs(rho - 0.74 * R) < 0.11
                  ? T.spot
                  : r >= 6 && rho > 0.22 && (Math.abs(th - 0.32) * rho < vw || Math.abs(th - 0.72) * rho < vw)
                    ? T.shade
                    : etch
                      ? null
                      : rho > 0.55 * R
                        ? T.lit
                        : T.shade;
        } else if (th > -1.5) {
          const R = 0.72 * (0.35 + 0.65 * Math.pow(Math.sin((Math.PI * (th + 1.5)) / 1.38), 0.7));
          if (rho <= R) col = rho > R - ed ? T.hindEdge : etch ? null : T.hind;
        }
      }
      if (!col) continue;
      if (col !== cur) {
        g.fillStyle = col;
        cur = col;
      }
      g.fillRect(cx + x, cy + y, 1, 1);
    }
  g.globalAlpha = 1;
}

/**
 * A wing-shaped cut round (cx, cy), radius `r`, from angle a0 to a1 (screen angles), squashed by `sy`: two lobes (the
 * fore wing big, a notch down to one row, the hind wing smaller) `w` rows deep at most; a white leading row, steel inside
 * crossed by three dark veins, a brocade spot, and a maroon silk lining on the inmost rows of the deep parts.
 */
function wingCut(g: G, cx: number, cy: number, r: number, a0: number, a1: number, w: number, lvl: number, sy = 1): void {
  if (lvl <= 0) return;
  const n = Math.max(10, Math.round(Math.abs(a1 - a0) * r * 1.6));
  const v1 = Math.round(0.2 * n);
  const v2 = Math.round(0.42 * n);
  const v3 = Math.round(0.8 * n);
  const sp = Math.round(0.3 * n);
  let cur = '';
  level(g, lvl);
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const lobe = u < 0.6 ? Math.pow(Math.sin((Math.PI * u) / 0.6), 0.7) : 0.72 * Math.pow(Math.sin((Math.PI * (u - 0.6)) / 0.4), 0.8);
    const rows = Math.max(1, Math.round(1 + (w - 1) * lobe));
    const vein = rows >= 4 && (i === v1 || i === v2 || i === v3);
    const a = a0 + (a1 - a0) * u;
    const ca = Math.cos(a);
    const sa = Math.sin(a) * sy;
    for (let k = rows - 1; k >= 0; k--) {
      const col =
        k === 0
          ? WH
          : rows >= 4 && k === rows - 1
            ? M3
            : rows >= 4 && k === rows - 2
              ? M5
              : vein
                ? ST3
                : i === sp && k === Math.round(rows * 0.4)
                  ? TR5
                  : k === 1
                    ? ST6
                    : ST5;
      if (col !== cur) {
        g.fillStyle = col;
        cur = col;
      }
      g.fillRect(Math.round(cx + ca * (r - k)), Math.round(cy + sa * (r - k)), 1, 1);
    }
  }
  g.globalAlpha = 1;
}

/**
 * A brocade silk ribbon along `at(u)` (u 0 = its held end), `w` rows deep where it faces you: it twists (the depth
 * swelling and thinning with `phase`), the face maroon with a light top row, the back gold brocade.
 */
function ribbon(g: G, n: number, at: (u: number) => V, w: number, phase: number, lvl: number): void {
  if (lvl <= 0) return;
  level(g, lvl);
  let p0 = at(0);
  for (let i = 1; i <= n; i++) {
    const u = i / n;
    const p = at(u);
    const tw = Math.sin(phase + u * 9);
    const taper = Math.min(1, (1 - u) * 3 + 0.3);
    const rows = Math.max(1, Math.round(w * (0.35 + 0.65 * Math.abs(tw)) * taper));
    const face = tw >= 0;
    for (let k = rows - 1; k >= 0; k--)
      line(g, [p0[0], p0[1] + k], [p[0], p[1] + k], k === 0 ? (face ? M6 : TR5) : face ? (k === rows - 1 ? M3 : M4) : TR3);
    p0 = p;
  }
  g.globalAlpha = 1;
}

/** A red silk thread from `a` to `b`, wavering by `amp` cells (more toward its free end). */
function thread(g: G, a: V, b: V, amp: number, phase: number, lvl: number, css = M5): void {
  if (lvl <= 0) return;
  const d = unit([b[0] - a[0], b[1] - a[1]]);
  const n = Math.max(4, Math.round(Math.hypot(b[0] - a[0], b[1] - a[1]) / 3));
  level(g, lvl);
  let p0 = a;
  for (let i = 1; i <= n; i++) {
    const u = i / n;
    const o = Math.sin(phase + u * 7) * amp * u;
    const q = lerp(a, b, u);
    const p: V = [q[0] - d[1] * o, q[1] + d[0] * o];
    line(g, p0, p, css);
    p0 = p;
  }
  g.globalAlpha = 1;
}

/**
 * A brocade lattice woven on the ground: an iso disc crossed by 2:1 diagonal threads every `pitch` cells (two cells per
 * row, the iso line), a rim ring. Walks the threads, not the disc.
 */
function lattice(g: G, c: V, rx: number, css: string, rim: string | null, lvl: number, pitch = 10): void {
  if (lvl <= 0 || rx < 2) return;
  level(g, lvl);
  const cx = Math.round(c[0]);
  const cy = Math.round(c[1]);
  g.fillStyle = css;
  for (let y = Math.ceil(-rx / 2) + 1; y < rx / 2; y++) {
    const half = rx * Math.sqrt(Math.max(0, 1 - ((2 * y) / rx) ** 2)) - 1;
    for (const off of [-2 * y, 2 * y]) {
      const k0 = Math.ceil((-half - off) / pitch);
      const k1 = Math.floor((half - 1 - off) / pitch);
      for (let k = k0; k <= k1; k++) g.fillRect(cx + k * pitch + off, cy + y, 2, 1);
    }
  }
  if (rim) isoRing(g, cx, cy, rx, rim);
  g.globalAlpha = 1;
}

/** Where the bleed marks pin on the foe: x cells off its middle, y in chest heights up. */
const MARK_AT: readonly V[] = [
  [-4, 1.3],
  [4, 0.95],
  [-1, 0.6],
  [5, 1.55],
  [-5, 0.35],
];
const markAt = (s: Scene, i: number): V => [s.f[0] + MARK_AT[i]![0], s.f[1] - MARK_AT[i]![1] * s.chest];
/** The bleed: `n` small crimson butterflies pinned on the foe, slowly opening and closing their wings. */
function marks(g: G, s: Scene, n: number, lvl: number, skip = -1): void {
  for (let i = 0; i < Math.min(n, MARK_AT.length); i++) {
    if (i === skip) continue;
    const open = 0.35 + 0.65 * Math.abs(Math.sin(s.t * 5 + i * 1.9));
    butterfly(g, markAt(s, i), 5, -Math.PI / 2 + (i % 2 ? 0.45 : -0.45), open, lvl, 'blood');
  }
}

/** Dazed: three small iron butterflies circling over the foe's head. */
function dazed(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const head: V = [s.f[0], s.f[1] - s.chest * 1.95];
  const lv = fade(e, life - 6, life - 3);
  for (let i = 0; i < 3; i++) {
    const a = e * 0.55 + (i * Math.PI * 2) / 3;
    butterfly(g, [head[0] + Math.cos(a) * 10, head[1] + Math.sin(a) * 3], 4, a + Math.PI / 2, Math.abs(Math.sin(e * 1.3 + i)), Math.sin(a) < 0 ? Math.min(lv, 2) : lv);
  }
}

/** An iron butterfly torn off a cut, flying up and away from `c` (`e` frames after), fluttering, dithered out. */
function flyOff(g: G, c: V, e: number, r: number, dir: number, life: number, kind: Kind = 'iron'): void {
  if (e < 0 || e >= life) return;
  const p: V = [c[0] + dir * e * 2.2 + Math.sin(e * 0.9) * 2, c[1] - e * 2.6];
  butterfly(g, p, r, -Math.PI / 2 + dir * 0.35, 0.25 + 0.75 * Math.abs(Math.cos(e * 1.1)), fade(e, life * 0.55, life * 0.8), kind);
}

/** A cut opening into wings on the foe: `e` frames after the blow a butterfly spreads out of it, then tears off and flies. */
function cutWings(g: G, c: V, rot: number, e: number, r: number, dir: number): void {
  if (e < 1) return;
  if (e <= 3) butterfly(g, c, r, rot, e === 1 ? 0.3 : e === 2 ? 0.7 : 1, 3);
  else flyOff(g, c, e - 4, Math.round(r * 0.75), dir, 10);
}

/** The blade's glint before a blow: a diamond on the tip (2 when charged), small steel wings forming on it. */
function tipGlint(g: G, s: Scene, t0: number, t1: number, wings = true): void {
  if (s.t < t0 || s.t >= t1) return;
  const { tip } = s.limbs(s.t);
  const k = clamp01((s.t - t0) / Math.max(0.05, t1 - t0));
  if (wings && k > 0.3) butterfly(g, [tip[0], tip[1] - 1], 3 + Math.round(k * 3), -Math.PI / 2, Math.abs(Math.sin(s.t * 14)), 3);
  level(g, 3);
  diamond(g, tip[0], tip[1], k < 0.5 ? 1 : 2, ST5, WH);
  g.globalAlpha = 1;
}

// ── 1. jy-cut: a quick sabre cut, the cut peels off the foe as an iron butterfly, a red thread pulled, one mark pinned ──

const CUT_HIT = 0.6;
const cut: ArtFx = {
  id: 'brocade-cut',
  skill: 'jy-cut',
  th: 'jy-cut',
  on: true,
  note: 'ประกายปลายดาบ ปีกเหล็กเล็กงอกที่ปลายดาบ -> ฟันเฉียงลงเป็นรอยรูปปีกผีเสื้อ (ขอบขาว ซับไหมแดง) -> รอยฟันลอกออกจากเป้าเป็นผีเสื้อเหล็กบินขึ้น ด้ายไหมแดงถูกดึงออกจากแผล ผีเสื้อแดงเล็กเกาะบนตัวเป้า (โลหิตไหล 1 ชั้น) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxCleave', t0: 0.2, t1: 1.0 }],
  hits: [CUT_HIT],
  ghosts: [0.5, 0.56],
  stepIn: 6,
  pre: 0.4,
  hit: CUT_HIT,
  end: 1.8,
  afterimage: 'port',
  over(g, s) {
    tipGlint(g, s, 0.22, 0.5);
    bladeArc(g, s, 0.42, 0.78, 'steel', 4);
    const e = since(s.t, CUT_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    // The wing cut across the foe, falling from the upper right, then torn off as a butterfly.
    if (e < 4) wingCut(g, c[0] - 8, c[1] - 2, 26, -Math.PI * 0.32, Math.PI * 0.72, e < 2 ? 10 : 5, fade(e, 2, 3));
    cutWings(g, [c[0] + 2, c[1] - 4], -Math.PI / 2 + 0.6, e, 13, 1);
    // The red thread pulled out of the wound and falling slack.
    if (e < 10) {
      const end: V = [c[0] + 10 + e * 2, c[1] + 2 + e * e * 0.18];
      thread(g, [c[0] - 1, c[1] + 1], end, 2.5, e * 0.9, fade(e, 6, 8));
    }
    if (e >= 2) marks(g, s, 1, e < 3 ? 2 : 3);
    hitStar(g, s, c, e, 'steel', 1.1);
  },
};

// ── 2. jy-cross (the lock): threads bind the foe, two wing cuts cross into a four-winged butterfly, it folds and pins ──

const X_HITS = [0.5, 0.88] as const;
const X_PIN = 1.25;
const cross: ArtFx = {
  id: 'brocade-cross',
  skill: 'jy-cross',
  th: 'jy-cross',
  on: true,
  note: 'ด้ายไหมแดงสองเส้นพุ่งไปพันเท้าเป้า วงไหมรัดรอบตัว (ตั้งท่าล็อก) -> ฟันเฉียงลงซ้าย ปีกเหล็กข้างหนึ่งค้าง -> ฟันเฉียงลงขวา ปีกอีกข้างประกบเป็นกากบาท = ผีเสื้อเหล็กสี่ปีกบนตัวเป้า -> ผีเสื้อหุบปีก ตอกตรึงเป้า (แทง) ผีเสื้อแดงเกาะสองตัว -> รอยกากบาทบนพื้นจาง',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.3, t1: 0.7 },
    { m: 'wxCleave', t0: 0.7, t1: 1.05 },
    { m: 'wxThrust', t0: 1.05, t1: 1.45 },
  ],
  hits: [...X_HITS, X_PIN],
  big: X_PIN,
  ghosts: [0.45, 0.83, 1.2],
  stepIn: 8,
  cast: [0.05, 0.4],
  pre: 0.4,
  hit: X_HITS[0],
  end: 2.4,
  afterimage: 'port',
  beats: [
    { t: 0.05, th: 'ด้ายรัด' },
    { t: X_HITS[0], th: 'ปีกซ้าย' },
    { t: X_HITS[1], th: 'ปีกขวา' },
    { t: X_PIN, th: 'หุบปีกตรึง' },
  ],
  under(g, s) {
    // The bind: a silk ring drawn round the foe's feet.
    if (s.t >= 0.15 && s.t < X_PIN + 0.1) {
      level(g, 3);
      ringOn(g, s.f, 13, Math.PI * 0.5, (s.t - 0.15) / 0.3, M5, true);
      g.globalAlpha = 1;
    }
    // The cross cut into the ground under the foe, fading.
    const e = since(s.t, X_PIN);
    if (e < 0 || e > 18) return;
    level(g, fade(e, 8, 13));
    for (const sg of [-1, 1]) {
      line(g, [s.f[0] - 16 * sg, s.f[1] - 6], [s.f[0] + 16 * sg, s.f[1] + 6], ST6);
      line(g, [s.f[0] - 16 * sg, s.f[1] - 5], [s.f[0] + 16 * sg, s.f[1] + 7], M4);
    }
    g.globalAlpha = 1;
  },
  over(g, s) {
    // The tell: two red threads shot from the blade to the foe and coiling round it.
    if (s.t >= 0.05 && s.t < X_HITS[0]) {
      const { tip } = s.limbs(s.t);
      const k = clamp01((s.t - 0.05) / 0.25);
      for (const sg of [-1, 1]) {
        const end: V = [lerp(tip, s.f, k)[0], lerp(tip, s.f, k)[1] - s.chest * (0.6 + 0.3 * sg) * k];
        thread(g, tip, end, 3, s.t * 9 + sg, 3, sg > 0 ? M5 : M6);
      }
    }
    tipGlint(g, s, 0.1, X_HITS[0], false);
    bladeArc(g, s, 0.35, 0.62, 'steel', 4);
    bladeArc(g, s, 0.75, 1.0, 'steel', 4);
    const c = strikeAt(s);
    const e1 = since(s.t, X_HITS[0]);
    const e2 = since(s.t, X_HITS[1]);
    const ep = since(s.t, X_PIN);
    // Each cut leaves a wing standing on the foe until the pin folds them.
    const hold = (e: number): number => (ep >= 0 ? (ep < 3 ? 3 : 0) : e < 2 ? 3 : 2);
    if (e1 >= 0 && ep < 3) wingCut(g, c[0] + 8, c[1] - 6, 24, Math.PI * 0.62, Math.PI * 1.32, e1 < 2 ? 8 : 5, hold(e1));
    if (e2 >= 0 && ep < 3) wingCut(g, c[0] - 8, c[1] - 6, 24, Math.PI * 0.38, -Math.PI * 0.32, e2 < 2 ? 8 : 5, hold(e2));
    // Both wings set: the four-winged butterfly on the foe; on the pin it folds shut and drives in.
    if (e2 >= 1 && ep < 6) {
      const open = ep < 0 ? 1 - 0.15 * Math.abs(Math.sin(s.t * 10)) : Math.max(0.15, 1 - ep * 0.3);
      butterfly(g, [c[0], c[1] - 3], ep < 0 ? 17 : 17 - ep * 2, -Math.PI / 2, open, ep < 3 ? 3 : 2);
    }
    if (ep >= 0) {
      if (ep < 8) {
        // Steel shards sprung out of the fold.
        level(g, fade(ep, 4, 6));
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2 + 0.4;
          const r = 6 + ep * 3.5;
          diamond(g, c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r * 0.7 + ep * ep * 0.15, 1, ST5, WH);
        }
        g.globalAlpha = 1;
      }
      marks(g, s, 2, ep < 2 ? 2 : 3);
    }
    hitStar(g, s, c, e1, 'steel', 0.9);
    hitStar(g, s, c, e2, 'steel', 0.9);
    hitStar(g, s, c, ep, 'steel', 1.4);
  },
};

// ── 3. jy-whirl: a swarm of iron butterflies flung to the picked spot, a cyclone of wing blades, marks on all inside ──

const WH_R = Math.min(64, tilesPx(2.4));
const WH_LAND = 0.75;
const whirl: ArtFx = {
  id: 'brocade-whirl',
  skill: 'jy-whirl',
  th: 'jy-whirl',
  on: true,
  note: 'วงไหมแดงวาดบอกจุดตก หมุนตัวฟัน ผีเสื้อเหล็กฝูงหนึ่งบินวนออกจากคมดาบไปยังจุดที่เลือก (ตั้งท่า 0.45 วิ) -> ตกลงกลายเป็นพายุหมุน ปีกเหล็กหกใบวนเป็นวงฟันทุกคนในวง ริบบิ้นไหมเกลียวขึ้น -> ผีเสื้อแดงเกาะทุกคนในวง (โลหิตไหล) -> ผีเสื้อแตกฝูงบินออก ลายทอบนพื้นจาง',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.1, t1: 0.5 },
    { m: 'wxRise', t0: 0.5, t1: 0.62 },
    { m: 'wxCleave', t0: 0.62, t1: 1.1 },
  ],
  hits: [WH_LAND],
  big: WH_LAND,
  ghosts: [0.3, 0.4, 0.5],
  stepIn: 3,
  cast: [0.05, 0.3],
  pre: 0.3,
  hit: WH_LAND,
  end: 2.3,
  afterimage: 'port',
  under(g, s) {
    // The landing mark (both sides see it): a silk ring drawn on round the spot.
    if (s.t >= 0.05 && s.t < WH_LAND + 0.1) {
      level(g, 3);
      ringOn(g, s.f, WH_R, -Math.PI / 2, (s.t - 0.05) / 0.45, M5, true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, WH_LAND);
    if (e < 0 || e > 20) return;
    // The cyclone's floor: the brocade weave, a fast ring sweeping out.
    // Solid, then two dithered frames, then gone (a dithered weave is many small pattern runs).
    if (e < 15) {
      // The weave in the middle (it costs a run per thread per row: kept to the inner half), the rim on the whole area.
      lattice(g, s.f, Math.round(WH_R * Math.min(0.55, 0.3 + e * 0.08)), TR3, null, e < 13 ? 3 : 2, 12);
      level(g, e < 13 ? 3 : 2);
      isoRing(g, s.f[0], s.f[1], WH_R, M4);
      g.globalAlpha = 1;
    }
    if (e < 6) {
      level(g, fade(e, 3, 5));
      isoRing(g, s.f[0], s.f[1], Math.round(WH_R * (0.3 + 0.75 * (e / 5))), WH, e < 3);
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    // The swarm: six butterflies spiralling off the blade and arcing over to the spot.
    if (s.t >= 0.25 && s.t < WH_LAND) {
      const { tip } = s.limbs(s.t);
      for (let i = 0; i < 6; i++) {
        const k = clamp01((s.t - 0.25 - i * 0.04) / 0.42);
        if (k <= 0 || k >= 1) continue;
        const a = i * 1.05 + k * 6;
        const base = lerp(tip, [s.f[0], s.f[1] - s.chest], k);
        const p: V = [base[0] + Math.cos(a) * 7, base[1] - Math.sin(Math.PI * k) * 26 + Math.sin(a) * 4];
        butterfly(g, p, 5, a, Math.abs(Math.sin(s.t * 16 + i)), 3);
      }
    }
    const e = since(s.t, WH_LAND);
    if (e < 0) return;
    // The cyclone: six iron wing blades whirling round the spot (back half dimmer), ribbons spiralling up the middle.
    if (e < 12) {
      const r = WH_R * (0.4 + 0.45 * clamp01((e + 1) / 4));
      const lv = fade(e, 7, 10);
      // The blade ring: three great wing cuts whirling flat round the spot at waist height.
      for (let k = 0; k < 3; k++) {
        const a0 = e * 0.8 + (k * Math.PI * 2) / 3;
        wingCut(g, s.f[0], s.f[1] - s.chest * 0.5, Math.round(r * 0.9), a0, a0 + 1.5, e < 8 ? 6 : 4, lv, 0.5);
      }
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + e * 0.75;
        const p: V = [s.f[0] + Math.cos(a) * r, s.f[1] + Math.sin(a) * r * 0.5 - s.chest * 0.7];
        const back = Math.sin(a) < 0;
                butterfly(g, p, back ? 5 : 8, a + Math.PI / 2, Math.abs(Math.cos(e * 1.2 + i)), lv);
      }
      for (let j = 0; j < 2; j++)
        ribbon(
          g,
          16,
          (u) => [s.f[0] + Math.cos(u * 7 + e * 0.8 + j * Math.PI) * (6 + u * 16), s.f[1] - u * s.chest * 2.4],
          4,
          e * 0.9 + j,
          fade(e, 7, 10),
        );
    }
    // The swarm breaking up and flying out.
    for (let i = 0; i < 6; i++) flyOff(g, [s.f[0] + (i - 2.5) * 10, s.f[1] - s.chest * (0.6 + (i % 3) * 0.3)], e - 11, 5, i < 3 ? -1 : 1, 10);
    if (e >= 2) marks(g, s, 2, e < 3 ? 2 : fade(e, 18, 20));
    hitStar(g, s, strikeAt(s), e, 'steel', 1.2);
  },
};

// ── 4. jy-lunge: steel wings spread from the shoulders, a gliding dash trailing a long silk ribbon, a flat cut on arrival ──

const LUNGE = 64;
const LU_GO = 0.5;
const LU_HIT = 0.72;
const lunge: ArtFx = {
  id: 'brocade-lunge',
  skill: 'jy-lunge',
  th: 'jy-lunge',
  on: true,
  note: 'ย่อตัว ปีกเหล็กคู่ใหญ่กางออกจากหลัง ประกายปลายดาบ (ตั้งท่า) -> พุ่งร่อนเข้าหาเป้า ริบบิ้นไหมแพรยาวสะบัดตามหลัง เงาร่างแยก -> ฟันตัดแนวนอนเต็มแรงเมื่อถึงตัว ปีกหุบ -> ริบบิ้นค่อยๆ ร่วงลงพาดพื้นแล้วจาง',
  weapon: 'sword',
  acts: [{ m: 'wxThrust', t0: 0.3, t1: 1.2 }],
  hits: [LU_HIT],
  big: LU_HIT,
  ghosts: [0.55, 0.6, 0.65],
  cast: [0.05, 0.5],
  pre: 0.5,
  hit: LU_HIT,
  end: 2.2,
  dash: LUNGE,
  afterimage: 'dash',
  move(s, t) {
    const k0 = clamp01((t - LU_GO) / 0.2);
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 1.9) / 0.3);
    return [s.d[0] * LUNGE * k * back, s.d[1] * LUNGE * k * back];
  },
  under(g, s) {
    // Wings behind the shoulders (under the figure): open on the wind-up, beating on the glide, folded on the blow.
    const b = s.body(s.t);
    const ew = since(s.t, LU_HIT);
    if (s.t >= 0.1 && ew < 3) {
      const k = clamp01((s.t - 0.1) / 0.3);
      const open = ew >= 0 ? 0.6 - ew * 0.2 : s.t < LU_GO ? k : 0.55 + 0.45 * Math.abs(Math.cos(s.t * 22));
      butterfly(g, [b[0] - s.d[0] * 10, b[1] - s.chest * 1.35], 10 + Math.round(14 * k), -Math.PI / 2 - s.d[0] * 0.5, open, ew >= 0 ? 2 : 3);
    }
    // The ribbon come down on the path, lying along it and dithering away.
    const e = since(s.t, LU_HIT + 0.35);
    if (e < 0 || e > 12) return;
    ribbon(g, 20, (u) => add([s.a[0], s.a[1] - 1], s.d, LUNGE - 4 - u * 56 + Math.sin(u * 5) * 2), 2, 1.2, fade(e, 6, 9));
  },
  over(g, s) {
    tipGlint(g, s, 0.15, LU_GO, false);
    // The ribbon streaming behind the hilt through the glide, falling slack after.
    const go = since(s.t, LU_GO);
    if (go >= 0 && go < 6) {
      const { hand } = s.limbs(s.t);
      const trail = Math.min(70, 30 + go * 14);
      const sag = go >= 3 ? (go - 2) * 3 : 0;
      ribbon(g, 22, (u) => [hand[0] - s.d[0] * trail * u, hand[1] + 2 + Math.sin(u * 8 - go * 1.6) * 5 * u - (16 - sag) * u * u], 5, go * 1.4, go < 4 ? 3 : 2);
    }
    // The flat cut across the foe at chest height, a long blade that thins to a glint.
    const e = since(s.t, LU_HIT);
    if (e >= 0 && e < 6) {
      const c = strikeAt(s, 1.05);
      const L = e < 2 ? 30 : 30 - (e - 2) * 6;
      level(g, fade(e, 3, 5));
      for (let k = 0; k < (e < 2 ? 4 : 2); k++)
        line(g, [c[0] - L, c[1] + k], [c[0] + L, c[1] + k + (k ? 0 : 0)], k === 0 ? WH : k === 1 ? ST6 : k === 3 ? M5 : ST4);
      g.globalAlpha = 1;
    }
    flyOff(g, strikeAt(s, 1.2), e - 1, 6, 1, 10);
    hitStar(g, s, strikeAt(s), e, 'steel', 1.2);
  },
};

// ── 5. jy-shatter (break): a brocade guard panel shows on the foe, a heavy chop splits it, lattice tiles and steel fly ──

const SH_HIT = 0.72;
/**
 * The foe's guard as a brocade panel: a gold lattice shield standing in front of it, `crack` 0..1 split down its middle,
 * the halves `apart` cells apart and falling.
 */
function panel(g: G, s: Scene, lvl: number, crack: number, apart = 0): void {
  const x0 = Math.round(s.f[0] - s.d[0] * 9);
  const top = Math.round(s.f[1] - s.chest * 1.75);
  const h = Math.round(s.chest * 1.55);
  const half = 7;
  level(g, lvl);
  for (let y = 0; y <= h; y++)
    for (let x = -half; x <= half; x++) {
      const seam = x + Math.round(Math.sin(y * 0.9) * 1.2);
      const gap = crack > 0 && Math.abs(seam) < 1 && y < h * crack + 2;
      if (gap) continue;
      const sd = seam < 0 ? -1 : 1;
      const xx = x0 + x + sd * apart;
      const yy = top + y + Math.round(Math.abs(x) * 0.25 + apart * apart * 0.12 - sd * apart * 0.3);
      const edge = Math.abs(x) === half || y === 0 || y === h || (apart > 0 && Math.abs(seam) === 1);
      if (edge) px(g, xx, yy, TR5);
      else if ((x + y) % 4 === 0 || (x - y + 64) % 4 === 0) px(g, xx, yy, (x + y) % 8 === 0 ? TR6 : TR3);
    }
  g.globalAlpha = 1;
}
const shatter: ArtFx = {
  id: 'brocade-shatter',
  skill: 'jy-shatter',
  th: 'jy-shatter',
  on: true,
  note: 'แผงลายทอทองปรากฏหน้าเป้า (การ์ดของเป้า) วงทองที่เท้า ขอบดาบเรืองทอง (ตั้งท่าทำลาย 0.45 วิ) -> ก้าวยกดาบฟันหนักลงตรงกลาง เงาร่างแยก -> แผงแตกกลาง ชิ้นลายทอและเศษเหล็กกระเด็น รอยแตกตารางบนพื้น -> ผีเสื้อเหล็กวนเหนือหัว (มึน) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBreak', t0: 0.1, t1: 1.2 }],
  hits: [SH_HIT],
  big: SH_HIT,
  ghosts: [0.56, 0.62, 0.68],
  stepIn: 10,
  cast: [0.05, 0.45],
  pre: 0.45,
  hit: SH_HIT,
  end: 2.5,
  afterimage: 'port',
  under(g, s) {
    // The break's tell: a gold ring at the caster's feet.
    if (s.t >= 0.05 && s.t < SH_HIT) {
      level(g, s.t < 0.5 ? 3 : 2);
      ringOn(g, s.a, 13, -Math.PI / 2, (s.t - 0.05) / 0.4, TR5, true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, SH_HIT);
    if (e < 0 || e > 20) return;
    // The ground cracked in a lattice under the foe, the weave torn open.
    lattice(g, s.f, Math.min(26, 10 + e * 5), ST5, ST3, fade(e, 10, 15), 8);
  },
  over(g, s) {
    // The guard panel standing on the foe through the wind-up, cracking on the blow.
    const e = since(s.t, SH_HIT);
    if (s.t >= 0.08 && e < 1) panel(g, s, s.t < 0.2 ? 2 : 3, 0);
    if (e >= 1 && e < 8) panel(g, s, fade(e, 5, 7), 1, e < 2 ? 0 : Math.round((e - 1) * 3));
    // The blade gilded: a gold row along it, a big glint.
    if (s.t >= 0.15 && e < 0) {
      const { hand, tip } = s.limbs(s.t);
      level(g, 3);
      line(g, lerp(hand, tip, 0.3), tip, TR5);
      diamond(g, tip[0], tip[1], s.t < 0.4 ? 1 : 2, TR4, WH);
      g.globalAlpha = 1;
    }
    bladeArc(g, s, 0.58, 0.95, 'steel', 5);
    if (e < 0) return;
    const c = strikeAt(s);
    // The chop: a tall wing cut falling through the panel.
    if (e < 4) wingCut(g, c[0] - 14, c[1] + 4, 32, -Math.PI * 0.42, Math.PI * 0.36, e < 2 ? 11 : 6, fade(e, 2, 3));
    // The panel's halves and tiles flung out: gold lattice squares and steel diamonds, falling.
    if (e >= 2 && e < 12) {
      level(g, fade(e, 7, 10));
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2 + hash(i, 300) * 0.4;
        const vv = 2.2 + hash(i, 301) * 1.8;
        const x = c[0] - s.d[0] * 6 + Math.cos(a) * vv * (e - 1);
        const y = c[1] + Math.sin(a) * vv * (e - 1) * 0.7 + 0.32 * (e - 1) * (e - 1);
        if (i % 3 === 0) diamond(g, x, y, 1, ST5, WH);
        else {
          g.fillStyle = i % 2 ? TR4 : TR3;
          g.fillRect(Math.round(x) - 2, Math.round(y) - 1, 4, 3);
          px(g, x, y, TR6);
          px(g, x - 1, y - 1, TR6);
        }
      }
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, e, 'gold', 1.4);
    dazed(g, s, e - 4, 18);
  },
};

// ── 6. jy-oath: the sabre raised in salute, a lattice woven under the feet, great iron wings unfold behind and fold in guard ──

const OA_SET = 0.85;
const oath: ArtFx = {
  id: 'brocade-oath',
  skill: 'jy-oath',
  th: 'jy-oath',
  on: true,
  note: 'ยกดาบตั้งตรงสาบาน ลายทอทองถักขึ้นใต้เท้าทีละวง สายสะพายไหมพันเกลียวขึ้นตามตัว -> ปีกเหล็กคู่ใหญ่กางขึ้นหลังตัว -> ปีกหุบมาด้านหน้าเป็นโล่ (ตั้งรับรอ 8 วิ) -> เหลือผีเสื้อทองสองตัวบินวนไหล่ ลายทอจางลง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 1.9 }],
  hits: [],
  pre: 0.1,
  hit: OA_SET,
  end: 2.7,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, 0.12);
    if (e < 0 || e > 30) return;
    // The weave growing ring by ring under the feet, then holding faint.
    const rx = Math.min(30, 8 + e * 3);
    lattice(g, s.a, rx, TR4, TR5, e < 18 ? 3 : fade(e - 18, 5, 9), 8);
    // The wings (under the figure): rising behind the shoulders, spread wide at the set, then folding shut (the guard).
    const b = s.body(s.t);
    const w = since(s.t, 0.35);
    if (w >= 0 && w < 16) {
      const grow = clamp01((w + 1) / 5);
      const open = w < 6 ? 1 - 0.1 * Math.abs(Math.sin(w * 1.1)) : Math.max(0.15, 1 - (w - 6) * 0.14);
      butterfly(g, [b[0] - s.d[0] * 6, b[1] - s.chest * 1.45], Math.round(12 + 20 * grow), -Math.PI / 2, open, w < 12 ? 3 : 2);
    }
  },
  over(g, s) {
    const b = s.body(s.t);
    // The sash: a ribbon winding up round the body.
    const r0 = since(s.t, 0.15);
    if (r0 >= 0 && r0 < 14) {
      const k = clamp01((r0 + 1) / 7);
      ribbon(
        g,
        20,
        (u) => [b[0] + Math.cos(u * 11 + r0 * 0.6) * 9, b[1] - 2 - u * k * s.chest * 2.1],
        2,
        r0 * 0.8,
        fade(r0, 9, 12),
      );
    }
    // The salute's glint.
    if (s.t >= 0.3 && s.t < OA_SET + 0.1) {
      const { tip } = s.limbs(s.t);
      level(g, 3);
      diamond(g, tip[0], tip[1], 2, TR4, WH);
      g.globalAlpha = 1;
    }
    // Two gold butterflies round the shoulders, waiting for the blow.
    const e = since(s.t, OA_SET + 0.4);
    if (e >= 0 && e < 18)
      for (let i = 0; i < 2; i++) {
        const a = e * 0.45 + i * Math.PI;
        butterfly(g, [b[0] + Math.cos(a) * 11, b[1] - s.chest * 1.35 + Math.sin(a) * 4], 5, a + Math.PI / 2, Math.abs(Math.sin(e * 1.2 + i)), Math.sin(a) < 0 ? 2 : fade(e, 13, 16), 'gold');
      }
  },
};

// ── 7. jy-execute (cash-out): the pinned butterflies called off the foe into the blade, one beheading cut, threads burst ──

const EX_HIT = 1.0;
const execute: ArtFx = {
  id: 'brocade-execute',
  skill: 'jy-execute',
  th: 'jy-execute',
  on: true,
  note: 'ผีเสื้อแดงที่เกาะเป้าอยู่ (ชั้นโลหิตไหล) ทีละตัวบินออกจากเป้า วนเข้าคมดาบ คมดาบแดงขึ้นทีละขั้น วงแดงรอบเท้าเป้า (ตั้งท่า) -> ฟันตัดแนวนอนเส้นเดียวยาวผ่านคอ นิ่งค้างหนึ่งจังหวะ -> ด้ายไหมแดงระเบิดพุ่งออกจากเป้ารอบทิศแล้วร่วงลง -> ด้ายจาง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 0.75 },
    { m: 'wxCleave', t0: 0.75, t1: 1.25 },
  ],
  hits: [EX_HIT],
  big: EX_HIT,
  ghosts: [0.9, 0.96],
  stepIn: 8,
  cast: [0.05, 0.75],
  pre: 0.7,
  hit: EX_HIT,
  end: 2.6,
  afterimage: 'port',
  beats: [
    { t: 0.1, th: 'เรียกผีเสื้อ' },
    { t: EX_HIT, th: 'ประหาร' },
    { t: 1.15, th: 'ด้ายแตก' },
  ],
  under(g, s) {
    if (s.t >= 0.1 && s.t < EX_HIT + 0.1) {
      level(g, s.t < 0.5 ? 2 : 3);
      ringOn(g, s.f, 15, -Math.PI / 2, (s.t - 0.1) / 0.5, M4, true);
      g.globalAlpha = 1;
    }
    // Threads lying slack round the foe after the burst.
    const e = since(s.t, EX_HIT + 0.5);
    if (e < 0 || e > 12) return;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.2;
      const p: V = [s.f[0] + Math.cos(a) * 10, s.f[1] + Math.sin(a) * 5];
      const q: V = [s.f[0] + Math.cos(a + 0.3) * 26, s.f[1] + Math.sin(a + 0.3) * 13];
      thread(g, p, q, 2, i, fade(e, 6, 9), i % 2 ? M5 : M4);
    }
  },
  over(g, s) {
    const { hand, tip } = s.limbs(s.t);
    // The marks left from earlier blows, called off one by one (0.12 s apart) along a curl into the blade.
    if (s.t < EX_HIT) {
      let gone = 0;
      for (let i = 0; i < 5; i++) {
        const t0 = 0.12 + i * 0.1;
        const k = clamp01((s.t - t0) / 0.32);
        if (k >= 1) {
          gone++;
          continue;
        }
        if (k <= 0) {
          const open = 0.35 + 0.65 * Math.abs(Math.sin(s.t * 5 + i * 1.9));
          butterfly(g, markAt(s, i), 5, -Math.PI / 2 + (i % 2 ? 0.45 : -0.45), open, 3, 'blood');
          continue;
        }
        const from = markAt(s, i);
        const to = lerp(hand, tip, 0.4 + i * 0.12);
        const p = lerp(from, to, k);
        const lift = Math.sin(Math.PI * k) * (14 + i * 3);
        butterfly(g, [p[0], p[1] - lift], 4 + Math.round(Math.sin(Math.PI * k) * 2), Math.atan2(to[1] - from[1], to[0] - from[0]), Math.abs(Math.sin(s.t * 18 + i)), 3, 'blood');
      }
      // The blade reddening by the number taken in.
      if (gone > 0) {
        level(g, 3);
        line(g, lerp(hand, tip, Math.max(0.15, 1 - gone * 0.18)), tip, M5);
        if (gone >= 3) diamond(g, tip[0], tip[1], gone >= 5 ? 2 : 1, M5, WH);
        g.globalAlpha = 1;
      }
    }
    bladeArc(g, s, 0.88, 1.12, 'maroon', 4);
    const e = since(s.t, EX_HIT);
    if (e < 0) return;
    const c = strikeAt(s, 1.45);
    // The cut: one long flat line through the neck, held a beat, then thinning.
    if (e < 5) {
      const L = e < 3 ? 40 : 40 - (e - 2) * 10;
      level(g, e < 3 ? 3 : 2);
      line(g, [c[0] - L, c[1] - 1], [c[0] + L, c[1] + 1], WH);
      if (e < 3) {
        line(g, [c[0] - L + 4, c[1]], [c[0] + L - 4, c[1] + 2], M5);
        line(g, [c[0] - L + 10, c[1] + 1], [c[0] + L - 10, c[1] + 3], M3);
      }
      g.globalAlpha = 1;
    }
    // The burst: red threads flung out of the foe on every side, arcing and falling slack.
    const b = e - 2;
    if (b >= 0 && b < 10) {
      const o: V = [s.f[0], s.f[1] - s.chest * 1.1];
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + hash(i, 400) * 0.4;
        const L = (14 + hash(i, 401) * 14) * clamp01((b + 1) / 3);
        const end: V = [o[0] + Math.cos(a) * L, o[1] + Math.sin(a) * L * 0.75 + b * b * 0.35];
        thread(g, o, end, 2.5, b * 0.8 + i, fade(b, 6, 8), i % 3 ? M5 : M6);
      }
      if (b < 6) butterfly(g, o, 13 - b, -Math.PI / 2, 1 - b * 0.15, fade(b, 3, 5), 'blood');
    }
    hitStar(g, s, [s.f[0] - s.d[0] * 3, s.f[1] - s.chest * 1.3], e, 'maroon', 1.7);
  },
};

// ── 8. jy-dragon (ท่าใหญ่): a brocade dragon of silk and iron wings rides the leap, two sky-splitting dives, a ground of wings ──

const D_R = Math.min(78, tilesPx(3));
const D_UP = 0.9;
const D_RISE = 0.95;
const D_HANG = 1.45;
const D_DIVE = 2.0;
const D_PASS = [2.3, 2.45, 2.6] as const;
const D_FIN = 2.95;
const D_KNEEL = 3.35;
const D_END = 4.3;
const D_H = 22;
const easeOut = (k: number): number => 1 - (1 - k) * (1 - k);
function dragonMove(s: Scene, t: number): V {
  const land = add(s.f, s.d, -24);
  const lx = land[0] - s.a[0];
  const ly = land[1] - s.a[1];
  if (t < D_RISE) return [0, 0];
  if (t < D_HANG) {
    const k = easeOut(clamp01((t - D_RISE) / (D_HANG - D_RISE)));
    return [lx * 0.35 * k, -D_H * k];
  }
  if (t < D_DIVE - 0.12) return [lx * 0.35, -D_H - Math.sin((t - D_HANG) * 9) * 1.5];
  if (t < D_DIVE) {
    const u = clamp01((t - (D_DIVE - 0.12)) / 0.12);
    return [lx * (0.35 + 0.65 * u), ly * u - D_H * (1 - u)];
  }
  const back = 1 - clamp01((t - 3.85) / 0.3);
  return [lx * back, ly * back];
}
/** The dragon: a long brocade ribbon from `tail` to the head `head`, coiling (`coil` turns of `rad` cells), iron fins. */
function dragonBody(g: G, tail: V, head: V, coil: number, rad: number, phase: number, lvl: number, fins = true): void {
  const at = (u: number): V => {
    const p = lerp(head, tail, u);
    const a = phase + u * coil * Math.PI * 2;
    return [p[0] + Math.cos(a) * rad * (0.4 + u * 0.6), p[1] + Math.sin(a) * rad * 0.45];
  };
  ribbon(g, 30, at, 6, phase, lvl);
  if (!fins) return;
  for (let i = 1; i <= 4; i++) {
    const u = i / 5;
    const p = at(u);
    const q = at(u + 0.03);
    butterfly(g, [p[0], p[1] - 3], 7 - Math.floor(i / 2), Math.atan2(q[1] - p[1], q[0] - p[0]) - Math.PI / 2, Math.abs(Math.sin(phase * 2 + i)), lvl);
  }
  // The head: an iron crest of wings with a white glint.
  butterfly(g, head, 9, -Math.PI / 2, 0.8 + 0.2 * Math.sin(phase * 3), lvl);
}
const dragon: ArtFx = {
  id: 'brocade-dragon',
  skill: 'jy-dragon',
  th: 'jy-dragon',
  on: true,
  note: 'ท่าใหญ่ ~4.3 วิ: ริบบิ้นไหมวนเข้าตัว วงพื้นที่สีแดง ลายทอทองใต้เท้า (รวมพลัง) -> ฟันเสยปีกเหล็กยกเป้า -> กระโดดสูง มังกรริบบิ้นแพรมีครีบปีกเหล็กพันเกลียวตามขึ้นฟ้า -> ค้างกลางอากาศ ปีกเหล็กยักษ์กางหลังตัว -> ดิ่งลงฟันผ่าฟ้า รอยปีกยาวสูง พื้นแตกลายตาราง เศษเหล็กกระเด็น -> ฟันปีกสามครั้งต่อเนื่อง ผีเสื้อเหล็กหลุดบินขึ้น -> ฟันปิดท้ายใหญ่ที่สุด มังกรริบบิ้นฟาดลงตามคมดาบ เงาปีกผีเสื้อยักษ์แผ่บนพื้น -> คุกเข่า ริบบิ้นร่วงลง ผีเสื้อแตกฝูง ศัตรูมึน',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 0.82 },
    { m: 'wxRise', t0: 0.82, t1: 1.0 },
    { m: 'wxRise', t0: 1.0, t1: 1.45 },
    { m: 'wxPower', t0: 1.45, t1: 1.9 },
    { m: 'wxBreak', t0: 1.7, t1: 2.25 },
    { m: 'wxCleave', t0: 2.2, t1: 2.4 },
    { m: 'wxRise', t0: 2.4, t1: 2.52 },
    { m: 'wxCleave', t0: 2.52, t1: 2.7 },
    { m: 'wxBreak', t0: 2.7, t1: 3.25 },
    { m: 'wxBuff', t0: 3.3, t1: 4.1 },
  ],
  hits: [D_UP, D_DIVE, ...D_PASS, D_FIN],
  big: D_FIN,
  ghosts: [0.98, 1.08, 1.92, 1.97, 2.32, 2.47, 2.62, 2.9],
  stepIn: 8,
  cast: [0.05, 0.85],
  pre: 0.8,
  hit: D_UP,
  end: D_END,
  afterimage: 'port',
  move: dragonMove,
  beats: [
    { t: 0.05, th: 'รวมพลัง' },
    { t: D_UP, th: 'ฟันเสย' },
    { t: D_RISE, th: 'มังกรขึ้นฟ้า' },
    { t: D_HANG, th: 'กางปีก' },
    { t: D_DIVE, th: 'ผ่าฟ้า' },
    { t: D_PASS[0], th: 'ปีกสามคม' },
    { t: D_FIN, th: 'มังกรฟาด' },
    { t: D_KNEEL, th: 'ริบบิ้นร่วง' },
  ],
  under(g, s) {
    // The hang: great iron wings spread behind the caster in the air (under the figure; placed off its two hands, which
    // the game reads from the frame it drew, so they follow the real leap).
    if (s.t >= D_HANG - 0.1 && s.t < D_DIVE - 0.04) {
      const { hand, handB } = s.limbs(s.t);
      const k = clamp01((s.t - D_HANG + 0.1) / 0.2);
      const mid = lerp(hand, handB, 0.5);
      butterfly(g, [mid[0] - s.d[0] * 10, mid[1] + 4], Math.round(12 + 12 * k), -Math.PI / 2, 1 - 0.2 * Math.abs(Math.sin(s.t * 12)), 3);
    }
    // The tell: the area ring drawn on round the caster, the weave woven under its feet.
    if (s.t >= 0.05 && s.t < D_DIVE) {
      const k = clamp01((s.t - 0.05) / 0.7);
      level(g, s.t < D_UP ? 3 : 2);
      ringOn(g, s.a, D_R, -Math.PI / 2, k, M5, k >= 1);
      g.globalAlpha = 1;
      lattice(g, s.a, Math.round(10 + 18 * k), TR4, TR5, s.t < D_RISE ? 3 : 2, 8);
    }
    // The first dive: the ground split in a lattice under the foe, a shock ring.
    const e1 = since(s.t, D_DIVE);
    if (e1 >= 0 && e1 < 12 && s.t < D_FIN) {
      lattice(g, s.f, Math.min(30, 12 + e1 * 6), ST5, ST3, fade(e1, 7, 10), 8);
      if (e1 < 6) {
        level(g, fade(e1, 3, 5));
        isoRing(g, s.f[0], s.f[1], Math.round(D_R * (0.25 + 0.6 * (e1 / 5))), WH, e1 < 3);
        g.globalAlpha = 1;
      }
    }
    // The finale: a giant iron butterfly's shadow spread flat on the ground, two shock rings, then fading out.
    const e = since(s.t, D_FIN);
    if (e < 0 || e > 26) return;
    const grow = clamp01((e + 1) / 4);
    butterfly(g, s.f, Math.round(30 + 22 * grow), -Math.PI / 2, 1, fade(e, 14, 20), 'iron', 0.5, true);
    for (let k = 0; k < 2; k++) {
      const ee = e - k * 2;
      if (ee < 0 || ee > 8) continue;
      level(g, fade(ee, 3, 6));
      isoRing(g, s.f[0], s.f[1], Math.round(D_R * (0.3 + 0.75 * clamp01(ee / 5))), k ? M5 : WH, k === 0);
    }
    g.globalAlpha = 1;
  },
  over(g, s) {
    const { tip } = s.limbs(s.t);
    // The gather: three ribbons spiralling in round the caster from past the area's edge.
    if (s.t >= 0.05 && s.t < D_UP) {
      const k = clamp01((s.t - 0.05) / 0.75);
      for (let j = 0; j < 3; j++) {
        const a0 = j * 2.1 + k * 5;
        const r = (D_R + 8) * (1 - k) + 10;
        ribbon(
          g,
          14,
          (u) => [s.a[0] + Math.cos(a0 + u * 1.6) * (r + u * 14), s.a[1] - s.chest * (0.5 + k) + Math.sin(a0 + u * 1.6) * (r + u * 14) * 0.45],
          3,
          s.t * 6 + j,
          k < 0.15 ? 2 : 3,
        );
      }
    }
    tipGlint(g, s, 0.4, D_UP);
    // The uproot: a rising wing cut on the foe.
    const eu = since(s.t, D_UP);
    if (eu >= 0 && eu < 4) {
      const c = strikeAt(s);
      wingCut(g, c[0] + 8, c[1] + 10, 26, Math.PI * 0.95, Math.PI * 1.6, eu < 2 ? 6 : 3, fade(eu, 2, 3));
    }
    // The rise and the hang: the dragon coiling up from the ground to the blade.
    if (s.t >= D_RISE && s.t < D_DIVE) {
      const k = clamp01((s.t - D_RISE) / 0.35);
      const tail: V = [s.a[0] - s.d[0] * 6, s.a[1]];
      const head: V = lerp(tail, [tip[0], tip[1] - 6], k);
      dragonBody(g, tail, head, 2.2, 18, s.t * 7, 3);
    }
    // The hang: the charged tip.
    if (s.t >= D_HANG - 0.1 && s.t < D_DIVE - 0.04) {
      level(g, 3);
      diamond(g, tip[0], tip[1], 2, ST5, WH);
      g.globalAlpha = 1;
    }
    bladeArc(g, s, D_DIVE - 0.12, D_DIVE + 0.15, 'steel', 5);
    const c = strikeAt(s);
    // The first dive: a tall wing cut from the sky through the foe, shards out, the wings burst into small butterflies.
    const e1 = since(s.t, D_DIVE);
    if (e1 >= 0 && e1 < 5) wingCut(g, c[0] - 20, c[1] - 10, 42, -Math.PI * 0.45, Math.PI * 0.42, e1 < 2 ? 7 : 4, fade(e1, 2, 4));
    if (e1 >= 0 && e1 < 10) {
      level(g, fade(e1, 6, 8));
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + hash(i, 500) * 0.4;
        const vv = 2.5 + hash(i, 501) * 1.8;
        diamond(g, c[0] + Math.cos(a) * vv * (e1 + 1), c[1] + Math.sin(a) * vv * (e1 + 1) * 0.65 + 0.3 * e1 * e1, 1, ST5, WH);
      }
      g.globalAlpha = 1;
    }
    for (let i = 0; i < 4; i++) flyOff(g, [c[0] - 12 + i * 8, c[1] - 8], e1 - 1 - (i % 2), 5, i < 2 ? -1 : 1, 9);
    // Three quick wing cuts at three angles, each shedding a butterfly.
    for (let j = 0; j < 3; j++) {
      const e = since(s.t, D_PASS[j]!);
      if (e < 0 || e >= 4) continue;
      const a0 = [Math.PI * 0.75, -Math.PI * 0.3, Math.PI * 1.1][j]!;
      wingCut(g, c[0] + Math.cos(a0 + Math.PI) * 10, c[1] + Math.sin(a0 + Math.PI) * 8, 22, a0, a0 + Math.PI * 0.85, e < 2 ? 5 : 3, fade(e, 2, 3));
      hitStar(g, s, c, e, 'steel', 0.9);
    }
    for (let j = 0; j < 3; j++) cutWings(g, [c[0] + (j - 1) * 9, c[1] - 8], -Math.PI / 2 + (j - 1) * 0.6, since(s.t, D_PASS[j]!), 10, j % 2 ? 1 : -1);
    // The finale: the dragon lashed down from the sky along the blade into the foe, the biggest wing cut, a burst.
    const ef = since(s.t, D_FIN - 0.1);
    if (ef >= 0 && ef < 4) {
      const k = clamp01((ef + 1) / 2);
      const head: V = lerp([s.f[0] - 30, s.f[1] - 112], [s.f[0], s.f[1] - s.chest], k);
      dragonBody(g, [s.f[0] - 70, s.f[1] - 118], head, 1.3, 10, s.t * 9, ef < 3 ? 3 : 2, ef < 2);
    }
    const e = since(s.t, D_FIN);
    if (e >= 0 && e < 6) wingCut(g, c[0] - 26, c[1] - 6, 52, -Math.PI * 0.5, Math.PI * 0.45, e < 2 ? 8 : 5, fade(e, 3, 5));
    if (e >= 1 && e < 12) {
      level(g, fade(e, 7, 10));
      for (let i = 0; i < 14; i++) {
        const a = (i / 14) * Math.PI * 2 + hash(i, 510) * 0.3;
        const vv = 3 + hash(i, 511) * 2;
        const x = c[0] + Math.cos(a) * vv * e;
        const y = c[1] + Math.sin(a) * vv * e * 0.6 + 0.3 * e * e;
        if (i % 2) diamond(g, x, y, 1, ST5, WH);
        else line(g, [x, y], [x + Math.cos(a) * 4, y + Math.sin(a) * 3], ST6);
      }
      g.globalAlpha = 1;
    }
    for (let i = 0; i < 6; i++) flyOff(g, [s.f[0] + (i - 2.5) * 12, s.f[1] - s.chest * (0.5 + (i % 3) * 0.35)], e - 3 - (i % 3), i % 2 ? 5 : 7, i < 3 ? -1 : 1, 14);
    hitStar(g, s, c, eu, 'steel', 1.0);
    hitStar(g, s, c, e1, 'steel', 1.5);
    hitStar(g, s, c, e, 'steel', 1.9);
    // The aftermath: ribbons drifting down over the area and lying, the foe dazed.
    const ek = since(s.t, D_KNEEL);
    if (ek >= 0 && ek < 14)
      for (let j = 0; j < 3; j++) {
        const x0 = s.f[0] + (j - 1) * 26;
        const y0 = s.f[1] - 70 + Math.min(62, ek * 7) + j * 4;
        ribbon(g, 14, (u) => [x0 - 12 + u * 24, y0 + Math.sin(u * 6 + ek * 0.7 + j) * 3], 2, ek * 0.6 + j, fade(ek, 9, 12));
      }
    dazed(g, s, e - 4, 18);
  },
};

/** The lamp-hook-sabre moves, hand-made (each its own shape, motion and timing; one palette and motif family). */
export const BROCADE_FX: readonly ArtFx[] = [cut, cross, whirl, lunge, shatter, oath, execute, dragon];

/** jy-dragon's sample times, for render/fx/longBrocade.ts to pin to the rules' beats. */
export const BROCADE_DRAGON_T = { up: D_UP, rise: D_RISE, hang: D_HANG, dive: D_DIVE, pass: D_PASS, fin: D_FIN, kneel: D_KNEEL, end: D_END } as const;
